import { createSelector } from '@reduxjs/toolkit';
import { getHasTicAiFeature } from 'redux/selectors/accounts';
import { getUserId } from 'redux/selectors/user';
import { lazySelector } from 'utils/reselect';
import { getTicAiPreferences } from '~domains/ai/predictions/redux/predictions.selectors';

const getIsTicAuthenticated = lazySelector(
  import('~redux/modules/tic/tic.auth.selectors').then(m => m.getIsTicAuthenticated)
);

/**
 * Is the AI feature *available* and *usable* to end-user?
 * Requires `ai_timesheet_creation` account feature and TIC authenticated.
 */
export const getIsTicAiFeatureAvailable = createSelector(
  [getHasTicAiFeature, getIsTicAuthenticated, getUserId],
  (ai_timesheet_creation, ticApiUsable, currentUserId): boolean => {
    /**
     *Force-enable for specific user IDs
     @link https://linear.app/timelyapp/issue/TT-2600/enable-tic-ai-for-10-users-in-everymatrix
     */
    const bypassFeatureCheckForUserIds = [
      2207161, 2234136, 2274154, 2274150, 2274153, 2223805, 2226917, 2237823, 2237875, 2247492,
      2247534,
    ];

    const aiFeatureAvailable =
      !!ai_timesheet_creation || bypassFeatureCheckForUserIds.includes(currentUserId);

    return !!ticApiUsable && aiFeatureAvailable;
  }
);

/**
 * Is the AI feature *currently, actually* active?
 * Checks feature availability, TIC is authenticated, and that AI `enabled: true`
 */
export const getIsTicAiCurrentlyActive = createSelector(
  [getIsTicAiFeatureAvailable, getTicAiPreferences],
  (featureAvailable, aiPreferences): boolean => {
    return featureAvailable && !!aiPreferences?.enabled;
  }
);
